import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getWebSocketUrl } from '../lib/api';

const RealtimeContext = createContext(null);

/**
 * One WebSocket per tab. Callers subscribe by event type via useRealtimeEvents.
 * `connectionGeneration` increments on each successful open so consumers can
 * refetch after reconnect (missed frames are not replayed).
 */
export function RealtimeProvider({ children }) {
  const listenersRef = useRef(new Set());
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const delayMsRef = useRef(1500);
  const closedRef = useRef(false);
  const [connectionGeneration, setConnectionGeneration] = useState(0);

  const dispatch = useCallback((data) => {
    if (!data?.type) return;
    listenersRef.current.forEach((entry) => {
      try {
        const types = entry.types;
        const ok = !types
          || (types instanceof Set ? types.has(data.type) : types.includes(data.type));
        if (ok) entry.handler(data);
      } catch {
        /* ignore subscriber errors */
      }
    });
  }, []);

  useEffect(() => {
    closedRef.current = false;

    const scheduleReconnect = () => {
      if (closedRef.current || reconnectTimerRef.current) return;
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        delayMsRef.current = Math.min(delayMsRef.current * 1.5, 15000);
        connect();
      }, delayMsRef.current);
    };

    const connect = () => {
      if (closedRef.current) return;
      try {
        const ws = new WebSocket(getWebSocketUrl());
        wsRef.current = ws;

        ws.onopen = () => {
          delayMsRef.current = 1500;
          setConnectionGeneration((n) => n + 1);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            dispatch(data);
          } catch {
            /* ignore malformed frames */
          }
        };

        ws.onerror = () => {
          try { ws.close(); } catch { /* ignore */ }
        };

        ws.onclose = () => {
          wsRef.current = null;
          scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      closedRef.current = true;
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
  }, [dispatch]);

  const subscribe = useCallback((eventTypes, handler) => {
    const entry = { types: eventTypes, handler };
    listenersRef.current.add(entry);
    return () => {
      listenersRef.current.delete(entry);
    };
  }, []);

  const value = useMemo(
    () => ({ subscribe, connectionGeneration }),
    [subscribe, connectionGeneration],
  );

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtimeHub() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtimeHub must be used within RealtimeProvider');
  }
  return ctx;
}

export default RealtimeContext;
